import React, { useEffect, useCallback, useMemo, useReducer, useRef } from 'react';
import { useLocation } from 'react-router';
import { LocalizationContext } from './context';
import {
  loadTranslations,
  createTranslateFunction,
  clearTranslationsCache,
  getBootstrapTranslationsForLanguage,
  ensureBootstrapTranslationsForLanguage,
  getCachedTranslationsForLanguage,
  getCachedLocaleVersion,
  fetchRuntimeLocaleMeta,
  getI18nDiagnostics,
  recordLocaleNotReadyFallback,
  resetI18nDiagnostics,
  markLocaleCacheActive,
  loadOptionalVocabulary,
  prefetchRouteTranslations,
} from './translations';
import {
  LocalizationContextType,
  LanguageCode,
  UseLocalizationReturn,
  SUPPORTED_LANGUAGES,
} from '@/types/i18n';
import {
  isSupportedLanguage,
  resolveInitialLanguage,
  savePreferredLanguage,
} from './languagePreference';
import {
  createInitialLocaleLifecycleState,
  localeLifecycleReducer,
  type LocaleActivation,
  type LocaleLifecycleEvent,
} from './localeStateMachine';
import {
  getPrefetchRouteDescriptors,
  getRouteLocaleDescriptor,
  type RouteLocaleDescriptor,
} from './routeLocaleManifest';
import {
  getLocaleSupportSnapshot,
  normalizeLocaleFailureReason,
  recordLocaleDiagnostic,
  type LocaleRuntimeRequestError,
} from './localeDiagnostics';

interface LocalizationProviderProps {
  children: React.ReactNode;
  defaultLanguage?: LanguageCode;
}

type LoadReason = 'select' | 'retry' | 'version_change' | 'route_change';

export function LocalizationProvider({
  children,
  defaultLanguage = 'en',
}: LocalizationProviderProps) {
  const location = useLocation();
  const routeDescriptor = useMemo(
    () => getRouteLocaleDescriptor(location.pathname),
    [location.pathname],
  );
  const initialLanguage = useMemo<LanguageCode>(
    () => resolveInitialLanguage(defaultLanguage),
    [defaultLanguage],
  );
  const initialCachedPayload = useMemo(
    () => getCachedTranslationsForLanguage(initialLanguage),
    [initialLanguage],
  );
  const initialBootstrap = useMemo(
    () => getBootstrapTranslationsForLanguage(initialLanguage),
    [initialLanguage],
  );
  const initialState = useMemo(
    () => createInitialLocaleLifecycleState({
      preferredLanguage: initialLanguage,
      translations: initialCachedPayload?.translations ?? initialBootstrap,
      version: initialCachedPayload?.version ?? getCachedLocaleVersion(),
      isVerifiedPayload: Boolean(initialCachedPayload),
      degradedFallback: initialCachedPayload
        ? null
        : (initialBootstrap ? 'bootstrap_bundle' : null),
      cacheSource: initialCachedPayload
        ? 'session_cache'
        : (initialBootstrap ? 'bootstrap_bundle' : null),
    }),
    [initialBootstrap, initialCachedPayload, initialLanguage],
  );

  const [state, dispatch] = useReducer(localeLifecycleReducer, initialState);
  const stateRef = useRef(initialState);
  const loadGenerationRef = useRef(initialState.generation);

  const transition = useCallback((event: LocaleLifecycleEvent) => {
    stateRef.current = localeLifecycleReducer(stateRef.current, event);
    dispatch(event);
  }, []);

  const performLoad = useCallback(async (
    targetLanguage: LanguageCode,
    generation: number,
    force: boolean,
    targetRoute: RouteLocaleDescriptor,
  ) => {
    const startedAt = performance.now();
    try {
      const result = await loadTranslations(targetLanguage, {
        force,
        route: targetRoute,
      });
      if (generation !== stateRef.current.generation) return;

      if (
        result.actualLanguage !== result.requestedLanguage
        && result.degradedFallback === null
      ) {
        recordLocaleDiagnostic({
          event_type: 'wrong_language_invariant',
          requested_locale: result.requestedLanguage,
          active_locale: result.actualLanguage,
          release_version: result.version,
          route_id: result.routeId,
          failure_reason: 'wrong_language',
          total_ms: performance.now() - startedAt,
        });
        throw new Error('Locale activation invariant failed');
      }

      const activation: LocaleActivation = {
        requestedLanguage: result.requestedLanguage,
        actualLanguage: result.actualLanguage,
        translations: result.translations,
        version: result.version,
        degradedFallback: result.degradedFallback,
        source: result.source,
        routeId: result.routeId,
      };
      transition({
        type: result.source === 'memory_cache' || result.source === 'session_cache'
          ? 'cache_hit'
          : 'fetch_success',
        generation,
        activation,
      });
      recordLocaleDiagnostic({
        event_type: 'switch_success',
        requested_locale: result.requestedLanguage,
        active_locale: result.actualLanguage,
        release_version: result.version,
        cache_source: result.source,
        route_id: result.routeId,
        total_ms: performance.now() - startedAt,
      });
      if (result.degradedFallback !== null) {
        recordLocaleDiagnostic({
          event_type: 'fallback_activated',
          requested_locale: result.requestedLanguage,
          active_locale: result.actualLanguage,
          release_version: result.version,
          cache_source: result.source,
          route_id: result.routeId,
          failure_reason: 'runtime_unavailable',
        });
      }
    } catch (err) {
      if (generation !== stateRef.current.generation) return;

      const failureReason = normalizeLocaleFailureReason(err);
      recordLocaleDiagnostic({
        event_type: 'switch_failure',
        requested_locale: targetLanguage,
        pending_locale: stateRef.current.pendingLanguage,
        active_locale: stateRef.current.activeLanguage,
        release_version: stateRef.current.observedVersion,
        route_id: targetRoute.id,
        failure_reason: failureReason,
        correlation_id: (err as LocaleRuntimeRequestError)?.correlationId ?? undefined,
        total_ms: performance.now() - startedAt,
      });

      transition({
        type: 'fetch_failure',
        generation,
        error: err instanceof Error ? err.message : 'Failed to load translations',
      });

      let bootstrap = getBootstrapTranslationsForLanguage(targetLanguage);
      if (!bootstrap) {
        try {
          bootstrap = await ensureBootstrapTranslationsForLanguage(targetLanguage);
        } catch {
          // Keep the currently active locale when the bootstrap chunk is also
          // unavailable.
        }
      }

      if (generation !== stateRef.current.generation) return;
      const currentState = stateRef.current;
      if ((!currentState.translations || !currentState.isInitialLocaleReady) && bootstrap) {
        transition({
          type: 'bootstrap_ready',
          generation,
          language: targetLanguage,
          translations: bootstrap,
        });
        recordLocaleDiagnostic({
          event_type: 'fallback_activated',
          requested_locale: targetLanguage,
          active_locale: targetLanguage,
          release_version: currentState.observedVersion,
          cache_source: 'bootstrap_bundle',
          route_id: targetRoute.id,
          failure_reason: failureReason,
        });
      }
    }
  }, [transition]);

  const startLoad = useCallback((
    targetLanguage: LanguageCode,
    reason: LoadReason,
    force = false,
    observedVersion?: string,
    targetRoute: RouteLocaleDescriptor = routeDescriptor,
  ) => {
    const generation = ++loadGenerationRef.current;
    const current = stateRef.current;
    recordLocaleDiagnostic({
      event_type: 'switch_started',
      requested_locale: targetLanguage,
      pending_locale: targetLanguage === current.activeLanguage ? null : targetLanguage,
      active_locale: current.activeLanguage,
      release_version: observedVersion ?? current.version,
      route_id: targetRoute.id,
    });
    if (reason === 'retry') {
      recordLocaleDiagnostic({
        event_type: 'retry',
        requested_locale: targetLanguage,
        active_locale: current.activeLanguage,
        release_version: current.version,
        route_id: targetRoute.id,
      });
    } else if (reason === 'version_change') {
      recordLocaleDiagnostic({
        event_type: 'version_change',
        requested_locale: targetLanguage,
        active_locale: current.activeLanguage,
        release_version: observedVersion,
        route_id: targetRoute.id,
      });
    }
    if (reason === 'select') {
      transition({ type: 'select', language: targetLanguage, generation });
    } else if (reason === 'version_change') {
      transition({
        type: 'version_change',
        generation,
        version: observedVersion ?? stateRef.current.observedVersion ?? '',
      });
    } else {
      transition({ type: 'retry', generation });
    }

    return performLoad(targetLanguage, generation, force, targetRoute);
  }, [performLoad, routeDescriptor, transition]);

  useEffect(() => {
    void startLoad(initialLanguage, 'select', false, undefined, routeDescriptor);
    // Initial activation is tied to the route visible at mount. Route changes
    // have a separate guarded effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLanguage]);

  useEffect(() => {
    const current = stateRef.current;
    if (!current.isInitialLocaleReady || !current.activeLanguage) return;
    if (current.activeRouteId === 'all' || current.activeRouteId === routeDescriptor.id) {
      return;
    }
    void startLoad(
      current.preferredLanguage,
      'route_change',
      false,
      undefined,
      routeDescriptor,
    );
  }, [routeDescriptor, startLoad]);

  const checkRuntimeVersion = useCallback(async () => {
    try {
      const meta = await fetchRuntimeLocaleMeta(true);
      const currentState = stateRef.current;
      const sameVersionRefreshInFlight = currentState.isLoading
        && meta.version === currentState.observedVersion;
      if (
        meta.version
        && meta.version !== currentState.version
        && !sameVersionRefreshInFlight
      ) {
        await startLoad(
          currentState.preferredLanguage,
          'version_change',
          true,
          meta.version,
          routeDescriptor,
        );
      }
    } catch {
      // Runtime locale refresh is opportunistic; the next focus/interval will retry.
    }
  }, [routeDescriptor, startLoad]);

  // Keep runtime locale version fresh without a persistent EventSource stream.
  // Firefox reports cleanly closed/rotated SSE streams as console errors under
  // HTTP/2, so the provider uses quiet meta checks on focus/visibility/interval.
  useEffect(() => {
    const interval = window.setInterval(() => {
      void checkRuntimeVersion();
    }, 60000);

    const onFocus = () => {
      void checkRuntimeVersion();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkRuntimeVersion();
      }
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void checkRuntimeVersion();
      }
    };

    const onOnline = () => {
      const currentState = stateRef.current;
      if (currentState.error && currentState.pendingLanguage) {
        void startLoad(currentState.preferredLanguage, 'retry', true);
        return;
      }
      void checkRuntimeVersion();
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkRuntimeVersion, startLoad]);

  const translateFn = useMemo(
    () => state.activeLanguage && state.translations
      ? createTranslateFunction(state.translations, state.activeLanguage, {
        requestedLanguage: state.preferredLanguage,
        localeReady: state.preferredLanguage === state.activeLanguage
          && state.degradedFallback === null,
      })
      : null,
    [
      state.activeLanguage,
      state.translations,
      state.preferredLanguage,
      state.degradedFallback,
    ],
  );

  const t = useCallback((
    key: string,
    params?: Record<string, string | number>,
    defaultValue?: string,
  ): string => {
    if (!translateFn) {
      recordLocaleNotReadyFallback(key, state.preferredLanguage, defaultValue);
      return defaultValue ?? key;
    }
    return translateFn(key, params, defaultValue);
  }, [state.preferredLanguage, translateFn]);

  const setLanguage = useCallback((newLang: LanguageCode) => {
    if (isSupportedLanguage(newLang)) {
      savePreferredLanguage(newLang);
      if (newLang !== stateRef.current.preferredLanguage) {
        void startLoad(newLang, 'select');
      }
    }
  }, [startLoad]);

  useEffect(() => {
    if (state.activeLanguage) {
      document.documentElement.lang = state.activeLanguage;
    }
  }, [state.activeLanguage]);

  useEffect(() => {
    markLocaleCacheActive(state.activeLanguage, state.version);
  }, [state.activeLanguage, state.version]);

  useEffect(() => {
    if (!state.activeLanguage || !state.version || state.version === 'bundle-fallback') {
      return;
    }
    let cancelled = false;
    void loadOptionalVocabulary(state.activeLanguage, state.version)
      .then((translations) => {
        if (!cancelled && translations) {
          transition({
            type: 'optional_ready',
            language: state.activeLanguage!,
            version: state.version!,
            translations,
          });
        }
      })
      .catch((error) => {
        console.warn('Optional PF2e vocabulary segment is unavailable:', error);
      });
    return () => { cancelled = true; };
  }, [state.activeLanguage, state.version, transition]);

  useEffect(() => {
    if (!state.activeLanguage || !state.version || state.version === 'bundle-fallback') {
      return;
    }
    const prefetchRoutes = getPrefetchRouteDescriptors(routeDescriptor);
    if (prefetchRoutes.length === 0) return;
    let cancelled = false;
    const prefetch = () => {
      if (cancelled) return;
      for (const descriptor of prefetchRoutes) {
        void prefetchRouteTranslations(
          state.activeLanguage!,
          state.version!,
          descriptor,
        ).catch(() => {
          // Prefetch is opportunistic and must never affect active locale.
        });
      }
    };
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(prefetch, { timeout: 2500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }
    const timeoutId = globalThis.setTimeout(prefetch, 500);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timeoutId);
    };
  }, [routeDescriptor, state.activeLanguage, state.version]);

  const reloadTranslations = useCallback(async () => {
    await startLoad(stateRef.current.preferredLanguage, 'retry', true);
  }, [startLoad]);

  const resetLocaleCache = useCallback(async () => {
    const current = stateRef.current;
    clearTranslationsCache();
    recordLocaleDiagnostic({
      event_type: 'cache_reset',
      requested_locale: current.preferredLanguage,
      active_locale: current.activeLanguage,
      release_version: current.version,
      route_id: current.activeRouteId,
    });
    await startLoad(current.preferredLanguage, 'retry', true);
  }, [startLoad]);

  const copyLocaleDiagnostics = useCallback(async (): Promise<boolean> => {
    const current = stateRef.current;
    const snapshot = getLocaleSupportSnapshot({
      requestedLanguage: current.preferredLanguage,
      pendingLanguage: current.pendingLanguage,
      activeLanguage: current.activeLanguage,
      releaseVersion: current.version,
      cacheSource: current.cacheSource,
      degradedReason: current.degradedFallback,
      isLoading: current.isLoading,
      routeId: current.activeRouteId,
    });
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
      recordLocaleDiagnostic({
        event_type: 'support_snapshot_copied',
        requested_locale: current.preferredLanguage,
        active_locale: current.activeLanguage,
        release_version: current.version,
        route_id: current.activeRouteId,
      });
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    window.__PF2E_I18N_DEV__ = {
      clearCache: clearTranslationsCache,
      reload: reloadTranslations,
      getVersion: () => getCachedLocaleVersion(),
      getDiagnostics: getI18nDiagnostics,
      resetDiagnostics: resetI18nDiagnostics,
      getState: () => {
        const currentState = stateRef.current;
        return {
          requestedLanguage: currentState.preferredLanguage,
          activeLanguage: currentState.activeLanguage,
          pendingLanguage: currentState.pendingLanguage,
          isInitialLocaleReady: currentState.isInitialLocaleReady,
          degradedFallback: currentState.degradedFallback,
          isLoading: currentState.isLoading,
          error: currentState.error,
          version: currentState.version,
          observedVersion: currentState.observedVersion,
          generation: currentState.generation,
          lastEvent: currentState.lastEvent,
          activeRouteId: currentState.activeRouteId,
        };
      },
    };

    return () => {
      delete window.__PF2E_I18N_DEV__;
    };
  }, [reloadTranslations]);

  const renderedLanguage = state.activeLanguage ?? state.preferredLanguage;
  const value: LocalizationContextType = useMemo(() => ({
    language: renderedLanguage,
    requestedLanguage: state.preferredLanguage,
    preferredLanguage: state.preferredLanguage,
    activeLanguage: state.activeLanguage,
    pendingLanguage: state.pendingLanguage,
    setLanguage,
    reloadTranslations,
    resetLocaleCache,
    copyLocaleDiagnostics,
    t,
    loadedLanguages: state.loadedLanguages,
    isLoading: state.isLoading,
    isInitialLocaleReady: state.isInitialLocaleReady,
    degradedFallback: state.degradedFallback,
    error: state.error,
    version: state.version,
    cacheSource: state.cacheSource,
  }), [copyLocaleDiagnostics, renderedLanguage, reloadTranslations, resetLocaleCache, setLanguage, state, t]);

  const isRouteLocaleReady = state.activeRouteId === 'all'
    || state.activeRouteId === routeDescriptor.id;
  if (!state.isInitialLocaleReady || !isRouteLocaleReady) {
    return (
      <div
        className="app-locale-loader"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="app-locale-loader__spinner" aria-hidden="true" />
      </div>
    );
  }

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLocalization(): UseLocalizationReturn {
  const context = React.useContext(LocalizationContext);

  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }

  const {
    language,
    requestedLanguage,
    preferredLanguage,
    activeLanguage,
    pendingLanguage,
    setLanguage,
    reloadTranslations,
    resetLocaleCache,
    copyLocaleDiagnostics,
    t,
    loadedLanguages,
    isLoading,
    isInitialLocaleReady,
    degradedFallback,
    error,
    version,
    cacheSource,
  } = context;

  const currentLanguage = useMemo(
    () => SUPPORTED_LANGUAGES.find(lang => lang.code === language),
    [language],
  );

  return {
    language,
    requestedLanguage,
    preferredLanguage,
    activeLanguage,
    pendingLanguage,
    setLanguage,
    reloadTranslations,
    resetLocaleCache,
    copyLocaleDiagnostics,
    t,
    currentLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    loadedLanguages,
    isLoading,
    isInitialLocaleReady,
    degradedFallback,
    error,
    version,
    cacheSource,
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTranslation(): (
  key: string,
  params?: Record<string, string | number>,
  defaultValue?: string,
) => string {
  const context = React.useContext(LocalizationContext);

  if (!context) {
    throw new Error('useTranslation must be used within a LocalizationProvider');
  }

  return context.t;
}
